<script setup lang="ts">
/**
 * 입시 소식 목록 `/news` (083 US1, FR-010) — 공식 발표 해설. 비로그인 열람, 인증 가드 없음.
 * 공지 목록(NoticesPage)과 같은 오프셋 페이징·고정 표시 패턴이며, 행마다 요약을 함께 보인다(검색 유입 독자가 고르기 쉽게).
 * 에러는 http.ts 인터셉터 일원화 — 여기 catch 금지.
 */
import { useQuery } from '@tanstack/vue-query';
import { Loader2, Newspaper, Pin } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import { fetchNewsList } from '#/api/news';
import { BaseBadge, BaseButton, EmptyState, PageShell, PageTitle } from '#/components/ui';
import { trackNewsListView } from '#/lib/analytics';
import { formatKstDate } from '#/lib/datetime';
import { NEWS_PAGE_SIZE, publicQueryKeys } from '#/queries/publicQueries';

/** 0-base 현재 페이지. */
const page = ref(0);

const {
  data: result,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: computed(() => publicQueryKeys.newsList(page.value, NEWS_PAGE_SIZE)),
  queryFn: () => fetchNewsList(page.value, NEWS_PAGE_SIZE),
});

const items = computed(() => result.value?.content ?? []);

/** 서버가 totalPages 를 안 내려줄 수 있어(PageResult 계약) totalElements 로 보정 계산. */
const totalPages = computed(() => {
  if (result.value?.totalPages != null) return result.value.totalPages;
  const total = result.value?.totalElements ?? 0;
  return Math.max(1, Math.ceil(total / NEWS_PAGE_SIZE));
});

const canPrev = computed(() => page.value > 0);
const canNext = computed(() => page.value + 1 < totalPages.value);

function goPrev() {
  if (canPrev.value) page.value -= 1;
}
function goNext() {
  if (canNext.value) page.value += 1;
}

onMounted(() => trackNewsListView());
</script>

<template>
  <PageShell variant="list">
    <PageTitle
      title="입시 소식"
      description="대학·교육 기관의 공식 발표를 알기 쉽게 정리해 드려요."
    />

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center gap-3 py-20 text-muted"
      role="status"
    >
      <Loader2 class="h-7 w-7 animate-spin text-brand-muted" />
      <span class="text-[13.5px]">입시 소식을 불러오는 중입니다…</span>
    </div>

    <EmptyState
      v-else-if="isError"
      title="입시 소식을 불러오지 못했습니다"
      description="잠시 후 다시 시도해 주세요."
    >
      <template #action>
        <BaseButton variant="line" size="md" @click="() => refetch()">다시 시도</BaseButton>
      </template>
    </EmptyState>

    <EmptyState
      v-else-if="items.length === 0"
      title="아직 등록된 입시 소식이 없습니다"
      description="새 발표가 나오면 이곳에 정리해 드릴게요."
    >
      <template #icon><Newspaper class="h-7 w-7" :stroke-width="1.6" /></template>
    </EmptyState>

    <template v-else>
      <ul class="divide-y divide-line rounded-card border border-line bg-white">
        <li v-for="item in items" :key="item.id">
          <RouterLink
            :to="`/news/${item.id}/`"
            class="block px-5 py-4 transition-colors hover:bg-bg-2 md:px-6"
            :class="item.isPinned ? 'bg-accent-soft/40' : ''"
          >
            <div class="flex items-center gap-3">
              <BaseBadge v-if="item.isPinned" type="accent" class="shrink-0">
                <Pin class="h-3 w-3" :stroke-width="2" />
                고정
              </BaseBadge>
              <p
                class="min-w-0 flex-1 truncate text-[14.5px] font-bold tracking-[-0.01em] text-brand"
              >
                {{ item.title }}
              </p>
              <span class="shrink-0 text-[12.5px] text-muted">{{
                formatKstDate(item.publishedAt)
              }}</span>
            </div>
            <p class="mt-1.5 line-clamp-2 text-[13px] leading-[1.6] text-body">
              {{ item.summary }}
            </p>
          </RouterLink>
        </li>
      </ul>

      <div v-if="totalPages > 1" class="mt-6 flex items-center justify-center gap-3">
        <BaseButton variant="line" size="sm" :disabled="!canPrev" @click="goPrev">이전</BaseButton>
        <span class="text-[12.5px] text-muted">{{ page + 1 }} / {{ totalPages }}</span>
        <BaseButton variant="line" size="sm" :disabled="!canNext" @click="goNext">다음</BaseButton>
      </div>
    </template>
  </PageShell>
</template>
